import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromLine, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useState, DragEvent, useEffect, useRef } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import constants from '@playcastdotio/GlobalConfig';

type ModalProps = {
  isOpen: boolean;
  authToken: string | null;
  onClose: () => void;
  onBannerSubmit: (bannerUrl: string) => void;
};

// Utility function to compress image
const compressImage = async (file: File, maxSizeKB = 1024): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions to maintain aspect ratio while reducing file size
      let { width, height } = img;
      const maxDimension = 2048; // Max width or height

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels to get under the size limit
      const tryCompress = (quality: number): void => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            const sizeKB = blob.size / 1024;

            if (sizeKB <= maxSizeKB || quality <= 0.1) {
              // Convert blob back to File
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              // Try with lower quality
              tryCompress(quality - 0.1);
            }
          },
          'image/jpeg',
          quality,
        );
      };

      tryCompress(0.8); // Start with 80% quality
    };

    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = URL.createObjectURL(file);
  });
};

// Utility function to create cropped image
const createCroppedImage = async (imageSrc: string, crop: Area): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Set canvas size to the cropped area
      canvas.width = crop.width;
      canvas.height = crop.height;

      // Draw the cropped portion of the image
      ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        0.8,
      );
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
};

export const BannerSelectionModal = ({ isOpen, authToken, onClose, onBannerSubmit }: ModalProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_UPLOAD_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  const TARGET_COMPRESSED_SIZE = 1024; // 1MB in KB

  // Supported image types
  const SUPPORTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.webp,.heic,.heif';

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  if (!isOpen) return null;

  const validateAndProcessFile = async (file: File) => {
    setError(null);
    setIsCompressing(true);

    try {
      if (file.size > MAX_UPLOAD_SIZE) {
        setSelectedFile(null);
        setError(`File is above the 50MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
        return;
      }

      if (!SUPPORTED_TYPES.includes(file.type)) {
        setSelectedFile(null);
        setError('Supported formats: JPEG, PNG, WebP, HEIC, HEIF');
        return;
      }

      // Compress the image if it's over the target size
      const fileSizeKB = file.size / 1024;
      let processedFile = file;

      if (fileSizeKB > TARGET_COMPRESSED_SIZE) {
        processedFile = await compressImage(file, TARGET_COMPRESSED_SIZE);
        console.log(`Compressed from ${fileSizeKB.toFixed(0)}KB to ${(processedFile.size / 1024).toFixed(0)}KB`);
      }

      setSelectedFile(processedFile);
      setError(null);
    } catch (err) {
      console.error('File processing error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process image');
      setSelectedFile(null);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndProcessFile(file);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setError(null);
    setIsUploading(false);
    setIsCompressing(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!previewUrl || !croppedAreaPixels) {
      setError('No image or crop area selected');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      if (!authToken) {
        throw new Error('Unable to get authentication token');
      }

      // Create cropped image blob
      const croppedImageBlob = await createCroppedImage(previewUrl, croppedAreaPixels);

      // Create FormData for upload
      const formData = new FormData();
      formData.append('image', croppedImageBlob, 'banner.jpg');

      // Upload to API
      const response = await fetch(`${constants.REALTIME_API_BASE_URL}realtime/images/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.imageUrl) {
        // Call the banner submit handler with the new URL
        onBannerSubmit(result.imageUrl);
        handleClose();
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Banner upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const isProcessing = isCompressing || isUploading;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='bg-darkest relative w-full max-w-xl rounded-lg p-6 text-center'>
        <button onClick={handleClose} className='absolute right-4 top-4 text-white hover:text-gray-300' disabled={isProcessing}>
          <FontAwesomeIcon icon={faXmark} className='size-6' />
        </button>
        <h2 className='mb-4 w-full text-left font-[Syne] text-2xl font-semibold text-amber-400'>Upload Banner Image</h2>

        {isCompressing && (
          <div className='mb-4 flex items-center justify-center gap-2 text-amber-400'>
            <div className='h-4 w-4 animate-spin rounded-full border-b-2 border-amber-400'></div>
            <span className='text-sm'>Compressing image...</span>
          </div>
        )}

        {previewUrl ? (
          <div className='flex flex-col items-center justify-center'>
            <div className='relative flex h-64 w-full items-center justify-center rounded-lg bg-black/30'>
              <Cropper
                image={previewUrl}
                crop={crop}
                zoom={zoom}
                aspect={16 / 3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                cropShape='rect'
                showGrid={true}
                style={{ containerStyle: { borderRadius: '0.5rem' } }}
                cropSize={{ width: 500, height: 112 }}
              />
            </div>
            <div className='mt-4 flex w-full items-center gap-4'>
              <label className='text-sm text-white'>Zoom</label>
              <input
                type='range'
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className='flex-1 accent-amber-400'
                disabled={isProcessing}
              />
            </div>
            {/* {selectedFile && <div className='mt-2 text-xs text-gray-400'>File size: {(selectedFile.size / 1024).toFixed(0)}KB</div>} */}
            <button
              className='text-darkest mt-6 w-full rounded-lg bg-amber-400 px-4 py-2 font-[Syne] text-lg font-bold hover:bg-amber-400/80 disabled:cursor-not-allowed disabled:opacity-50'
              type='button'
              onClick={handleConfirm}
              disabled={isProcessing || !croppedAreaPixels}
            >
              {isUploading ? 'Uploading...' : 'Confirm'}
            </button>
            <button
              className='mt-2 text-sm text-amber-300 underline hover:text-amber-200 disabled:opacity-50'
              type='button'
              onClick={() => setSelectedFile(null)}
              disabled={isProcessing}
            >
              Change image
            </button>
          </div>
        ) : (
          <div className='flex flex-col items-center gap-4'>
            <div
              className={`flex h-64 w-full items-center justify-center rounded-lg border-2 border-dashed border-amber-400 transition-colors ${isDragging ? 'bg-primary/80 border-amber-300' : ''} bg-primary`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className='flex flex-col items-center'>
                <FontAwesomeIcon icon={faArrowUpFromLine} className='mb-2 size-16 text-white' />
                <p className='mb-2 font-[Syne] text-xl text-white'>Drag and drop image</p>
                <p className='text-sm text-gray-400'>Supports JPEG, PNG, WebP, HEIC, HEIF</p>
                <p className='text-xs text-gray-500'>Up to 24MB (auto-compressed to 1MB)</p>
              </div>
            </div>
            <button
              className='text-darkest w-fit rounded-md bg-amber-400 px-4 py-2 font-[Syne] text-base font-bold hover:bg-amber-400/80'
              type='button'
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              Upload from computer
            </button>
            <input
              ref={fileInputRef}
              type='file'
              accept={ACCEPTED_EXTENSIONS}
              id='banner-upload-input'
              className='hidden'
              onChange={handleFileInputChange}
            />
          </div>
        )}
        {error && <p className='mt-2 text-red-500'>{error}</p>}
      </div>
    </div>
  );
};
